import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { StringParam } from 'use-query-params'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { ShareLoadProvider, useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useShipmentOffersApi } from 'apps/fleet-planner/hooks/useShipmentOffersApi'
import { useTodosApi } from 'apps/fleet-planner/hooks/useTodosApi'
import { MarketplaceLoadDetailsModalContainer } from 'apps/fleet-planner/pages/marketplace/MarketplaceLoadDetailsContainer'
import { dismissShareLoadToast } from 'apps/fleet-planner/utils/actions/modals'
import { declineShipmentOffer } from 'apps/fleet-planner/utils/api'
import { modalInfoSelector } from 'apps/fleet-planner/utils/selectors'
import { useQueryParams } from 'utils/hooks'
import { ShareLoadToastMessage, PAGE } from '../driver-led-status/ShareLoadToastMessage'
import { LoadDetailsModal } from '../load-details/LoadDetailsModal'
import { OfferRowContainer } from '../offers/OfferRowContainer'
import { SetupGuideContainer } from '../setup-guide/SetupGuideContainer'
import { ShareLoadModal } from '../share-load/ShareLoadModal'
import { ShareLoadToastWrapper } from '../share-load/ShareLoadToastWrapper'
import { DashboardButtons } from './DashboardButtons'
import { GreetingHeader } from './GreetingHeader'
import { TodosListRow } from './Todos/TodosListRow'
import { NoLoadsToAccept } from './svg/NoLoadsToAccept'
import { NoTodos } from './svg/NoTodos'

const useDashboardStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(7),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    overflow: 'scroll',

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(5),
      paddingRight: theme.spacing(6),
      paddingLeft: theme.spacing(6),
    },
  },
  shipmentOffers: {
    order: 2,
    [theme.breakpoints.up('md')]: {
      order: 1,
    },
  },
  shortcuts: {
    order: 1,
    [theme.breakpoints.up('md')]: {
      order: 2,
    },
  },
  emptyCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  card: {
    padding: 0,
  },
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.5),

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(3),
    },
  },
  header2: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1.5),
  },
  shortcutsHeader: {
    paddingBottom: theme.spacing(1.5),
  },
}))

export const DashboardPage = () => {
  const { trackEvent } = useFMSAnalytics()
  const { shareLoads } = useSelector(modalInfoSelector)
  const { message = '', shipmentId: shareLoadShipmentId = '' } = shareLoads || {}
  const { showActivationToast } = useActivationContext()
  const { isMobile } = useBreakpointContext()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFmsOnboardingEnabled } = getFlag('fmsOnboarding')

  const { todos = [], getTodos, isLoading: isTodosLoading } = useTodosApi()

  const {
    allShipmentOffers = [],
    getAllShipmentOffers,
    isLoading: isAllShipmentOffersLoading,
    shipmentOfferParam,
  } = useShipmentOffersApi()

  const classes = useDashboardStyles()
  const { userDetails } = useUserDetails()
  const { firstName = '' } = userDetails || {}
  const history = useHistory()
  const dispatch = useDispatch()

  // If user accesses the page from a direct modal-open URL, set the modalId
  const { action, shipmentId } = useParams<{
    shipmentId: string
    action: string
  }>()
  const [{ declineOfferStatus }] = useQueryParams({
    declineOfferStatus: StringParam,
  })

  const [modalLoadId, setModalLoadId] = useState<string | undefined>(shipmentId)
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  // Boolean flags to detect which modal is open
  const marketplaceMatch = useRouteMatch('/home/:shipmentId/book')
  const loadDetailsMatch = useRouteMatch('/home/:shipmentId/details')
  const isLoadDetailsModalOpen = loadDetailsMatch && modalLoadId
  const isMPLoadDetailsModalOpen = marketplaceMatch && modalLoadId

  // Show confirmation toast message after being redirected from Activation flow.
  useEffectOnce(() => {
    if (showActivationToast) {
      openToast({
        toastType: 'default',
        toastMessage: `Thank you for submitting your application. We'll notify you when we've reviewed your carrier.`,
      })
    }
  })

  /**
   * If user arrives from email button for decline
   */
  useEffectOnce(() => {
    if (declineOfferStatus === 'success') {
      openToast({
        toastType: 'default',
        toastMessage: 'Successfully declined load.',
        onClose: () => {
          history.replace('/home')
        },
      })
      trackEvent('Reserved Load', 'RESERVED_SHIPMENT_DECLINED')
    } else if (declineOfferStatus === 'failure') {
      openToast({
        toastType: 'alert',
        toastMessage: 'Failed to decline load. Please try again',
        onClose: () => {
          history.replace('/home')
        },
      })
      trackEvent('Reserved Load', 'RESERVED_SHIPMENT_DECLINED_FAILED')
    }
  })

  useEffectOnce(() => {
    if (!isTodosLoading) {
      getTodos()
    }

    if (!isAllShipmentOffersLoading) {
      getAllShipmentOffers()
    }
  })

  const NoLoadsToAcceptCard = () => (
    <Card>
      <Box className={classes.emptyCard}>
        <NoLoadsToAccept />
      </Box>
      <Typography variant='subtitle1' color='textSecondary' component='div' align='center'>
        No loads yet
      </Typography>
      <Typography variant='subtitle2' color='textSecondary' component='div' align='center'>
        Loads tendered to you will appear here.
      </Typography>
    </Card>
  )

  const EmptyTodoListCard = () => (
    <Card>
      <Box className={classes.emptyCard}>
        <NoTodos />
      </Box>
      <Typography variant='subtitle1' color='textSecondary' component='div' align='center'>
        Youre all caught up!
      </Typography>
      <Typography variant='subtitle2' color='textSecondary' component='div' align='center'>
        Loads that need your attention will appear here.
      </Typography>
    </Card>
  )

  const todoRowClickHandler = (id: string) => {
    setModalLoadId(id)
    history.push(`/home/${id}/details`)
  }

  const acceptLoadRowClickHandler = (id: string) => {
    setModalLoadId(id)
    history.push(`/home/${id}/book?${shipmentOfferParam}=1`)
  }

  const viewAllOffersHandler = () => {
    history.push('/offers')
  }

  const closeModalHandler = () => {
    history.push('/home')
    setModalLoadId(undefined)
    getAllShipmentOffers()
    // refresh the list of todos and offers when a modal is closed
    getTodos()
  }

  const declineShipmentHandler = async (declinedShipmentId: number) => {
    const declinedId = await declineShipmentOffer(declinedShipmentId)

    if (declinedId !== undefined) {
      openToast({
        toastType: 'default',
        toastMessage: 'Declined load.',
      })
      trackEvent('Reserved Load', 'RESERVED_SHIPMENT_DECLINED')
      closeModalHandler()
    } else {
      openToast({
        toastType: 'alert',
        toastMessage: 'Failed to decline load. Please try again',
      })
    }
  }

  const declineShipmentHandlerNew = (response: string = '') => {
    // declining an offer will be handled within the component following the release of DL O4
    // this function only displays a toast on success and refreshes the offers list
    if (response === 'success') {
      openToast({
        toastType: 'default',
        toastMessage: <span>Offer has been successfully declined.</span>,
      })
      getAllShipmentOffers()
    } else {
      openToast({
        toastType: 'alert',
        toastMessage: <span>Offer decline unsuccessful. Please try again.</span>,
      })
    }
  }

  const { pathname = '' } = useLocation()

  const handleShareLoadClose = () => {
    history.push(pathname.replace('/share', ''))
  }

  const closeShareLoadToastHandler = () => dispatch(dismissShareLoadToast())

  const setupGuide = (
    <Box data-test='setup-guide' flex='0 0 100%'>
      <SetupGuideContainer firstName={firstName} />
    </Box>
  )

  return (
    <>
      {isLoadDetailsModalOpen && (
        <LoadDetailsModal
          id={modalLoadId}
          onClose={closeModalHandler}
          path='/home/:shipmentId/details'
        />
      )}
      {isMPLoadDetailsModalOpen && (
        <MarketplaceLoadDetailsModalContainer
          route='home'
          onModalClose={closeModalHandler}
          onShipmentDecline={declineShipmentHandler}
        />
      )}
      {isMobile && <TopNav title='Home' />}
      <Box className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={12} className='pb0'>
            <GreetingHeader firstName={firstName} />
          </Grid>
          {/**
           * Setup Guide
           */}
          {isFmsOnboardingEnabled && isMobile && (
            <Box p={2} pb={0} display='flex'>
              {setupGuide}
            </Box>
          )}
          <Grid item xs={12} md={9} className={classes.shipmentOffers}>
            {/**
             * Setup Guide
             */}
            {isFmsOnboardingEnabled && !isMobile && setupGuide}
            {/**
             * Loads
             */}
            <Box data-test='loads'>
              <Box display='flex' alignItems='center'>
                <Typography
                  className={classes.header}
                  variant='subtitle1'
                  color='textPrimary'
                  component='div'>
                  Loads Offered To You
                </Typography>
                <Box pt={isMobile ? 0 : 2} marginLeft='auto'>
                  <Button onClick={viewAllOffersHandler} color='tertiary'>
                    View All Offers
                  </Button>
                </Box>
              </Box>
              {isAllShipmentOffersLoading && (
                <Skeleton variant='rectangular' height={195} width='100%' />
              )}
              {!isAllShipmentOffersLoading && (
                <div>
                  {allShipmentOffers.length > 0 ? (
                    <Card className={classes.card}>
                      {allShipmentOffers.map(offer => (
                        <OfferRowContainer
                          onClick={acceptLoadRowClickHandler}
                          key={offer.externalId}
                          variant='home'
                          shipmentOffer={offer}
                          onDeclineHandler={declineShipmentHandlerNew}
                        />
                      ))}
                    </Card>
                  ) : (
                    <NoLoadsToAcceptCard />
                  )}
                </div>
              )}
            </Box>
            {/**
             * Todos
             */}
            <Box data-test='todos'>
              <Typography
                className={classes.header2}
                variant='subtitle1'
                color='textPrimary'
                component='div'>
                To-Dos
              </Typography>
              {todos.length > 0 ? (
                <Card className={classes.card}>
                  {todos.map(todo => (
                    <TodosListRow key={todo.externalId} todo={todo} onClick={todoRowClickHandler} />
                  ))}
                </Card>
              ) : (
                <EmptyTodoListCard />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={3} className={classes.shortcuts}>
            {!isMobile && (
              <Typography
                className={classes.shortcutsHeader}
                variant='subtitle1'
                color='textPrimary'
                component='div'>
                Shortcuts
              </Typography>
            )}
            <DashboardButtons />
          </Grid>
        </Grid>
      </Box>
      {action === 'share' && (
        <ShareLoadProvider>
          <ShareLoadToastWrapper />
          <ShareLoadModal loadId={modalLoadId} isOpen onClose={handleShareLoadClose} />
        </ShareLoadProvider>
      )}
      <ShareLoadToastMessage
        page={PAGE.loads}
        shipmentId={shareLoadShipmentId}
        message={message}
        onClose={closeShareLoadToastHandler}
      />
      <Toast
        variation={toastType}
        open={toastOpen}
        onClose={closeToast}
        message={toastMessage}
        multiLine={isMobile}
      />
    </>
  )
}

export default DashboardPage
